import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import Button from '@/components/ui/Button';
import HoverHint from '@/components/ui/Tooltip/HoverHint';
import { MultiSelectOption } from '@/components/ui/CheckboxMultiSelect/CheckboxMultiSelect';
import CreateFundGroupDialog from './CreateFundGroupDialog';
import FeatureFlagHistory from './FeatureFlagHistory';
import TargetingPicker, { TargetingValue } from './TargetingPicker';
import FeatureAnnouncementsSection, { momentOf } from './FeatureAnnouncementsSection';
import { SYSTEM_MESSAGES_MANAGEMENT_URL } from '@/pages/ManagementArea/SystemMessages/systemMessages';
import { SystemMessageDtoIn } from '@/models/in/SystemMessageDtoIn';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { delAuth, postAuth, putAuth } from '@/api/ApiCalls';
import { FeatureLifecycleStatus } from '@/models/common/FeatureLifecycleStatus';
import { FundClassification } from '@/models/common/FundClassification';
import { Role } from '@/models/common/Role';
import { FeatureFlagDetailDtoIn } from '@/models/in/FeatureFlagDtoIn';
import { FundGroupDtoIn } from '@/models/in/FundGroupDtoIn';
import { FundGroupDtoOut } from '@/models/out/FundGroupDtoOut';
import { FeatureFlagUpdateDtoOut, FeatureFlagTargetingDtoOut } from '@/models/out/FeatureFlagDtoOut';
import Fund from '@/models/Fund';
import { saveReleaseDraft, takeReleaseDraft } from './releaseDraft';
import styles from './EditFeatureFlagDialog.module.scss';

const BASE_URL = 'management/feature-flags';

interface UserRow {
  email: string;
  firstName?: string;
  lastName?: string;
}

interface EditFeatureFlagDialogProps {
  featureKey: string;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /** Back from editing the announcement: put the release's unsaved choices back (releaseDraft). */
  restoreDraft?: boolean;
}

const STATUS_ORDER: FeatureLifecycleStatus[] = [
  FeatureLifecycleStatus.NotReleased,
  FeatureLifecycleStatus.InRollout,
  FeatureLifecycleStatus.FullyReleased,
];

const EMPTY_TARGETING: TargetingValue = { fundIds: [], groupIds: [], userEmails: [], classifications: [], roles: [] };

const sameSet = <T,>(a: T[], b: T[]) => a.length === b.length && a.every((v) => b.includes(v));
const sameTargeting = (a: TargetingValue, b: TargetingValue) =>
  sameSet(a.fundIds, b.fundIds) && sameSet(a.groupIds, b.groupIds) && sameSet(a.userEmails, b.userEmails) && sameSet(a.classifications, b.classifications) && sameSet(a.roles, b.roles);

/** What the one window shows: the release itself, or one of the places it leads to and back. */
type View = 'release' | 'history' | 'newGroup';
/** A question the footer asks in place, instead of a second window over the dialog. */
type Asking = 'wide' | 'group' | 'finalize' | 'unfinalize' | 'retire' | 'unretire' | null;

/**
 * A flag's release (HAT-1489 rework): one screen that says what will open, to whom, and what will be
 * sent with it, and a line beside «שמירה» that sums the save up. One front per flag: a Nedarim
 * feature is a row of its own, released from the Nedarim tab with its own targeting (HAT-10195).
 *
 * One window, never two. The history and a new group open IN this window, with «חזרה לשחרור» - the
 * release's unsaved choices wait meanwhile, because this component stays mounted. The confirmations
 * (a wide release, saving a selection as a group, ending the release, leaving with unsaved changes)
 * are asked in the footer, where the button that triggered them was.
 *
 * The announcement is read here, not written: its wording is edited on «הודעות למשתמשים», where
 * every message is (Chaya, 25.10). «עריכה» and «+ הוספת הודעה» lead there.
 */
const EditFeatureFlagDialog = ({ featureKey, open, onClose, onSuccess, restoreDraft = false }: EditFeatureFlagDialogProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser } = useAuthContext();
  const headers = { Authorization: `Bearer ${currentUser?.token}` };

  const { data: detail, refetch: refetchDetail } = useGetAuthApi<FeatureFlagDetailDtoIn>(`${BASE_URL}/${featureKey}`);
  const { data: funds, isLoading: fundsLoading } = useGetAuthApi<Fund[]>('funds');
  const { data: users, isLoading: usersLoading } = useGetAuthApi<UserRow[]>('users');
  const { data: groups, isLoading: groupsLoading, refetch: refetchGroups } = useGetAuthApi<FundGroupDtoIn[]>('management/fund-groups');

  /*
   * The flag's announcements, read off the management list - one per audience, shipped by the
   * feature's PR. Shown here so whoever releases the feature reads what will go out with it.
   */
  const { data: allMessages } = useGetAuthApi<SystemMessageDtoIn[]>(open ? SYSTEM_MESSAGES_MANAGEMENT_URL : '');
  const announcements = useMemo(
    () => (allMessages ?? []).filter((message) => message.featureFlagKey === featureKey),
    [allMessages, featureKey],
  );

  const [view, setView] = useState<View>('release');

  const [status, setStatus] = useState<FeatureLifecycleStatus>(FeatureLifecycleStatus.NotReleased);
  const [description, setDescription] = useState('');
  const [editingDescription, setEditingDescription] = useState(false);
  const [targeting, setTargeting] = useState<TargetingValue>(EMPTY_TARGETING);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [asking, setAsking] = useState<Asking>(null);
  const [groupName, setGroupName] = useState('');

  // Initialize form state once per open - so a background refetch of `detail` (e.g. on reconnect)
  // does not wipe the admin's unsaved edits.
  const initializedRef = useRef(false);
  useEffect(() => {
    if (!open) {
      initializedRef.current = false;
      return;
    }
    if (detail && !initializedRef.current) {
      initializedRef.current = true;
      setStatus(detail.lifecycleStatus);
      setDescription(detail.description ?? '');
      setEditingDescription(false);
      setTargeting({
        fundIds: detail.fundIds ?? [],
        groupIds: detail.groupIds ?? [],
        userEmails: detail.userEmails ?? [],
        classifications: detail.classifications ?? [],
        roles: detail.roles ?? [],
      });
      // Back from editing the announcement: the choices made before leaving, not the saved ones.
      const draft = restoreDraft ? takeReleaseDraft(featureKey) : null;
      if (draft) {
        setStatus(draft.status);
        setDescription(draft.description);
        setTargeting(draft.targeting);
      }
      setView('release');
      setAsking(null);
      setError(null);
    }
  }, [open, detail, restoreDraft, featureKey]);

  const realFunds = useMemo(() => (funds ?? []).filter((f) => f.id !== -1), [funds]);

  const fundOptions: MultiSelectOption<number>[] = useMemo(
    // A fund with no name still has to be pickable - show its number rather than a blank row.
    () => realFunds.map((f) => ({ value: f.id, label: f.name || `#${f.id}`, sublabel: `#${f.id}` })),
    [realFunds],
  );
  const userOptions: MultiSelectOption<string>[] = useMemo(
    () => (users ?? []).map((u) => ({
      value: u.email,
      label: [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email,
      sublabel: u.email,
    })),
    [users],
  );
  const groupOptions: MultiSelectOption<number>[] = useMemo(
    () => (groups ?? []).map((g) => ({ value: g.id, label: g.name, sublabel: t('featureFlags.summary.members', { count: g.membersCount }) })),
    [groups, t],
  );
  const classificationOptions: MultiSelectOption<string>[] = useMemo(
    () => Object.values(FundClassification).map((c) => ({ value: c, label: t(`featureFlags.classificationLabels.${c}`, c) })),
    [t],
  );
  const roleOptions: MultiSelectOption<string>[] = useMemo(
    () => Object.values(Role).map((r) => ({ value: r, label: t(`featureFlags.roleLabels.${r}`, r) })),
    [t],
  );

  /* A fund counts as reached if the rollout for that status would reach it through any of the pickers. */
  const fundsReachedByStatus = (s: FeatureLifecycleStatus): Set<number> => {
    if (s === FeatureLifecycleStatus.NotReleased) return new Set();
    if (s === FeatureLifecycleStatus.FullyReleased) return new Set(realFunds.map((f) => f.id));
    const classificationSet = new Set(targeting.classifications);
    const directFundSet = new Set(targeting.fundIds);
    const groupFundSet = new Set<number>();
    (groups ?? [])
      .filter((g) => targeting.groupIds.includes(g.id))
      .forEach((g) => g.fundIds.forEach((id) => groupFundSet.add(id)));
    return new Set(
      realFunds
        .filter(
          (f) => (f.classification && classificationSet.has(f.classification as FundClassification))
            || directFundSet.has(f.id)
            || groupFundSet.has(f.id),
        )
        .map((f) => f.id),
    );
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const siteCoverageCount = useMemo(() => fundsReachedByStatus(status).size, [status, realFunds, targeting, groups]);

  const isSiteWideImpact = status === FeatureLifecycleStatus.FullyReleased || siteCoverageCount >= 50;
  const savedStatus = detail ? (detail.finalizedAt ? FeatureLifecycleStatus.FullyReleased : detail.lifecycleStatus) : null;
  const savedTargeting: TargetingValue = detail
    ? { fundIds: detail.fundIds ?? [], groupIds: detail.groupIds ?? [], userEmails: detail.userEmails ?? [], classifications: detail.classifications ?? [], roles: detail.roles ?? [] }
    : EMPTY_TARGETING;
  const statusChanged = !!detail && status !== detail.lifecycleStatus;
  const targetingChanged = !!detail && !sameTargeting(targeting, savedTargeting);
  const descriptionChanged = !!detail && description !== (detail.description ?? '');
  const widens = statusChanged && status !== FeatureLifecycleStatus.NotReleased && (savedStatus === FeatureLifecycleStatus.NotReleased || status === FeatureLifecycleStatus.FullyReleased);
  // A cancelled feature is restored before it is edited, so a status changed behind it cannot surprise anyone.
  const isLocked = !!detail?.finalizedAt || !!detail?.retiredAt;
  const canSave = !isSubmitting && !!detail && !isLocked;
  const isNedarim = detail?.client === 'Nedarim';

  const showTargeting = status === FeatureLifecycleStatus.InRollout;

  // Whether the manually picked funds/users already correspond to an existing group,
  // so we know when it's worth offering to save the selection as a new one.
  const matchingGroupExists = useMemo(() => {
    if (targeting.fundIds.length === 0 && targeting.userEmails.length === 0) return true;
    const fundSet = new Set(targeting.fundIds);
    const emailSet = new Set(targeting.userEmails);
    return (groups ?? []).some((g) => (
      g.fundIds.length === fundSet.size
      && g.userEmails.length === emailSet.size
      && g.fundIds.every((id) => fundSet.has(id))
      && g.userEmails.every((email) => emailSet.has(email))
    ));
  }, [targeting, groups]);

  // Accepts an explicit targeting override so a just-created group can be saved immediately,
  // without waiting for the setTargeting state update it triggered to land first.
  const doSaveFeature = async (targetingOverride?: { fundIds: number[]; userEmails: string[]; groupIds: number[] }) => {
    const picked = targetingOverride ?? targeting;
    setError(null);
    setIsSubmitting(true);
    try {
      // Save targeting FIRST, then status. If status (the second call) fails, the flag keeps its
      // previous lifecycle state (targeting-only changes are inert unless InRollout), so a partial
      // failure never leaves the feature exposed wider than intended - the admin just retries.
      const targetingBody: FeatureFlagTargetingDtoOut = { classifications: targeting.classifications, roles: targeting.roles, fundIds: picked.fundIds, userEmails: picked.userEmails, groupIds: picked.groupIds };
      await putAuth<FeatureFlagDetailDtoIn, object, FeatureFlagTargetingDtoOut>(`${BASE_URL}/${featureKey}/targeting`, headers, {}, targetingBody);

      const statusBody: FeatureFlagUpdateDtoOut = { lifecycleStatus: status, description };
      await putAuth<FeatureFlagDetailDtoIn, object, FeatureFlagUpdateDtoOut>(`${BASE_URL}/${featureKey}`, headers, {}, statusBody);

      onSuccess();
      onClose();
    } catch {
      setError(t('featureFlags.dialog.saveError'));
      setAsking(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  const continueSave = () => {
    if (showTargeting && !matchingGroupExists) {
      setGroupName('');
      setAsking('group');
      return;
    }
    setAsking(null);
    doSaveFeature();
  };

  // A wide release is confirmed as a step, with what it means for the users, not as a checkbox in the form.
  const handleSave = () => {
    const wideNow = isSiteWideImpact && (statusChanged || targetingChanged);
    if (wideNow) {
      setAsking('wide');
      return;
    }
    continueSave();
  };

  // The picked funds/users become a group, and the feature targets the group instead of them.
  const saveAsGroupAndSave = async () => {
    if (!groupName.trim()) {
      setError(t('fundGroups.dialog.nameRequired'));
      return;
    }
    setError(null);
    setIsSubmitting(true);
    try {
      const body: FundGroupDtoOut = { name: groupName.trim(), description: '', fundIds: targeting.fundIds, userEmails: targeting.userEmails };
      const created = await postAuth<FundGroupDtoIn, FundGroupDtoOut>('management/fund-groups', headers, body);
      const nextGroupIds = [...targeting.groupIds, created.id];
      setTargeting({ ...targeting, groupIds: nextGroupIds, fundIds: [], userEmails: [] });
      setAsking(null);
      await doSaveFeature({ fundIds: [], userEmails: [], groupIds: nextGroupIds });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '';
      setError(message.includes('already exists') ? t('fundGroups.dialog.duplicateName') : t('fundGroups.dialog.saveError'));
      setIsSubmitting(false);
    }
  };

  /*
   * Ending the release, cancelling the feature, and taking either back. Each is a step of its own on
   * the server, never part of «שמירה»: finishing locks the feature open, cancelling closes it to all.
   */
  const confirmEnd = async (kind: 'finalize' | 'unfinalize' | 'retire' | 'unretire') => {
    setIsSubmitting(true);
    setError(null);
    try {
      const path = kind === 'finalize' || kind === 'unfinalize' ? 'finalize' : 'retire';
      if (kind === 'finalize' || kind === 'retire') await postAuth(`${BASE_URL}/${featureKey}/${path}`, headers);
      else await delAuth(`${BASE_URL}/${featureKey}/${path}`, headers);
      setAsking(null);
      initializedRef.current = false;
      await refetchDetail();
      onSuccess();
    } catch {
      setError(t('featureFlags.actionError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalFunds = realFunds.length;
  const coverageLine = (s: FeatureLifecycleStatus, count: number) => {
    if (s === FeatureLifecycleStatus.NotReleased) return t('featureFlags.dialog.coverageNone');
    if (s === FeatureLifecycleStatus.FullyReleased) return t('featureFlags.dialog.coverageAll', { total: totalFunds.toLocaleString() });
    return t('featureFlags.dialog.coverage', { n: count.toLocaleString(), total: totalFunds.toLocaleString() });
  };

  /** What the save will do, in one line beside the button. Empty parts are left out; nothing changed says so. */
  const summary = (): string => {
    const parts: string[] = [];
    if (statusChanged || (targetingChanged && status === FeatureLifecycleStatus.InRollout)) {
      const front = isNedarim ? 'nedarim' : 'site';
      if (status === FeatureLifecycleStatus.NotReleased) parts.push(t(`featureFlags.dialog.summary.${front}Closes`));
      else if (status === FeatureLifecycleStatus.FullyReleased) parts.push(t(`featureFlags.dialog.summary.${front}OpensAll`));
      else parts.push(t(`featureFlags.dialog.summary.${front}Opens`, { n: siteCoverageCount.toLocaleString() }));
    }
    if (announcements.length > 0 && (statusChanged || (targetingChanged && status === FeatureLifecycleStatus.InRollout))) {
      const moment = momentOf(savedStatus, status);
      if (moment === 'onSave' || moment === 'widening') parts.push(t('featureFlags.dialog.summary.announcementGoes'));
      else if (moment === 'retracting') parts.push(t('featureFlags.dialog.summary.announcementGone'));
    }
    if (parts.length === 0 && descriptionChanged) parts.push(t('featureFlags.dialog.summary.descriptionOnly'));
    return parts.length === 0 ? t('featureFlags.dialog.summary.nothing') : parts.join(' · ');
  };

  const segmented = (value: FeatureLifecycleStatus, onPick: (s: FeatureLifecycleStatus) => void, disabled = false) => (
    <div className={styles.segmented} role="radiogroup">
      {STATUS_ORDER.map((s) => (
        <button
          key={s}
          type="button"
          role="radio"
          aria-checked={value === s}
          disabled={disabled}
          onClick={() => { onPick(s); setAsking(null); }}
          className={`${styles.segment} ${value === s ? styles.segmentActive : ''} body-sm-semibold`}
        >
          {t(`featureFlags.status.${s}`)}
        </button>
      ))}
    </div>
  );

  const picker = (
    <TargetingPicker
      value={targeting}
      onChange={(next) => { setTargeting(next); setAsking(null); }}
      fundOptions={fundOptions}
      groupOptions={groupOptions}
      userOptions={userOptions}
      classificationOptions={classificationOptions}
      roleOptions={roleOptions}
      groups={groups ?? []}
      onCreateGroup={() => setView('newGroup')}
      loading={{ funds: fundsLoading, users: usersLoading, groups: groupsLoading }}
    />
  );

  const backToRelease = () => setView('release');

  const dirty = statusChanged || targetingChanged || descriptionChanged;
  /*
   * A round trip, in this tab: the announcement's editor opens on «הודעות למשתמשים», and saving or
   * closing it comes straight back here - with whatever was picked before leaving, kept meanwhile.
   */
  const openMessage = (message: SystemMessageDtoIn | null) => {
    const back = `returnTo=${encodeURIComponent(featureKey)}`;
    const url = message
      ? `/management/system-messages?message=${message.id}&${back}`
      : `/management/system-messages?newFor=${encodeURIComponent(featureKey)}&${back}`;
    if (dirty) saveReleaseDraft(featureKey, { status, description, targeting });
    onClose();
    navigate(url);
  };

  /** The footer: the summary and «שמור», or - while one is pending - the question it asks. */
  const footer = () => {
    if (asking === 'wide') {
      return (
        <div className={`${styles.footer} ${styles.footerAsking}`} role="alertdialog" aria-labelledby="ff-ask">
          <span id="ff-ask" className="body-sm-semibold">
            {t('featureFlags.dialog.wide.title', { n: siteCoverageCount.toLocaleString() })}{' '}
            <span className="body-sm-regular">{announcements.length > 0 && widens ? t('featureFlags.dialog.wide.withAnnouncement') : t('featureFlags.dialog.wide.message')}</span>
          </span>
          <span className={styles.footerSpacer} />
          <Button variant="primary" onClick={continueSave} disabled={isSubmitting}>{t('featureFlags.dialog.wide.confirm')}</Button>
          <Button variant="white" onClick={() => setAsking(null)} disabled={isSubmitting}>{t('cancel')}</Button>
        </div>
      );
    }
    if (asking === 'group') {
      return (
        <div className={`${styles.footer} ${styles.footerAsking}`} role="alertdialog" aria-labelledby="ff-ask">
          <span id="ff-ask" className="body-sm-semibold">{t('featureFlags.saveAsGroup.inline')}</span>
          <input
            type="text"
            className={`${styles.groupName} body-sm-regular`}
            value={groupName}
            onChange={(e) => { setGroupName(e.target.value); if (error) setError(null); }}
            placeholder={t('featureFlags.saveAsGroup.namePlaceholder')}
            aria-label={t('fundGroups.columns.name')}
          />
          <span className={styles.footerSpacer} />
          <Button variant="primary" onClick={saveAsGroupAndSave} disabled={isSubmitting || !groupName.trim()}>{isSubmitting ? t('saving') : t('featureFlags.saveAsGroup.saveWithGroup')}</Button>
          <Button variant="white" onClick={() => { setAsking(null); doSaveFeature(); }} disabled={isSubmitting}>{t('featureFlags.saveAsGroup.saveWithout')}</Button>
        </div>
      );
    }
    if (asking === 'finalize' || asking === 'unfinalize' || asking === 'retire' || asking === 'unretire') {
      return (
        <div className={`${styles.footer} ${styles.footerAsking}`} role="alertdialog" aria-labelledby="ff-ask">
          <span id="ff-ask" className="body-sm-regular">{t(`featureFlags.${asking}Dialog.message`)}</span>
          <span className={styles.footerSpacer} />
          <Button variant="primary" onClick={() => confirmEnd(asking)} disabled={isSubmitting}>{t(`featureFlags.${asking}`)}</Button>
          <Button variant="white" onClick={() => setAsking(null)} disabled={isSubmitting}>{t('cancel')}</Button>
        </div>
      );
    }
    return (
      <div className={styles.footer}>
        <Button variant="primary" onClick={handleSave} disabled={!canSave}>
          {isSubmitting ? t('saving') : t('save')}
        </Button>
        <Button variant="white" onClick={onClose} disabled={isSubmitting}>{t('close')}</Button>
        <span className={`${styles.summary} body-sm-regular`} aria-live="polite">{summary()}</span>
        <span className={styles.footerSpacer} />
        <button type="button" className={`${styles.link} body-sm-semibold`} onClick={() => setView('history')}>{t('featureFlags.history')}</button>
        {/* Only a feature already open to everyone can be finished - finishing never widens a rollout. */}
        {detail && !isLocked && detail.lifecycleStatus === FeatureLifecycleStatus.FullyReleased && (
          <button type="button" className={`${styles.link} body-sm-semibold`} onClick={() => setAsking('finalize')}>{t('featureFlags.finalize')}</button>
        )}
        {/* For a feature that is no longer needed: closed to all and off the list, until restored. */}
        {detail && !isLocked && (
          <button type="button" className={`${styles.link} ${styles.danger} body-sm-semibold`} onClick={() => setAsking('retire')}>{t('featureFlags.retire')}</button>
        )}
      </div>
    );
  };

  const name = detail ? detail.displayName : featureKey;
  const titles: Record<View, string> = {
    release: name,
    history: t('featureFlags.historyView.title', { name }),
    newGroup: t('fundGroups.dialog.createTitle'),
  };

  const backLink = (
    <button type="button" className={`${styles.back} body-sm-semibold`} onClick={backToRelease}>
      → {t('featureFlags.dialog.backToRelease', { name })}
    </button>
  );

  return (
    <MainDialogLayout open={open} onOpenChange={(isOpen: boolean) => { if (!isOpen) onClose(); }} title={titles[view]} maxWidth="1120px" contentClassName={styles.fixedSize}>
      <div className={styles.body}>
        {view === 'history' && (
          <>
            {backLink}
            <FeatureFlagHistory featureKey={featureKey} />
          </>
        )}

        {view === 'newGroup' && (
          <>
            {backLink}
            <CreateFundGroupDialog
              embedded
              fundOptions={fundOptions}
              userOptions={userOptions}
              onClose={backToRelease}
              onCreated={(groupId) => {
                setTargeting((prev) => ({ ...prev, groupIds: [...prev.groupIds, groupId] }));
                refetchGroups();
                backToRelease();
              }}
            />
          </>
        )}

        {view === 'release' && (
          <>
            {/* The PR's description, read as text; a pencil for the rare fix. The key beside it, for the developer. */}
            <div className={styles.description}>
              <span className={styles.key} dir="ltr">{featureKey}</span>
              {editingDescription ? (
                <input
                  id="ff-desc"
                  type="text"
                  autoFocus
                  className={`${styles.descriptionInput} body-sm-regular`}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  onBlur={() => setEditingDescription(false)}
                  placeholder={t('featureFlags.dialog.descriptionPlaceholder')}
                />
              ) : (
                <>
                  <span className={`${styles.descriptionText} body-sm-regular`}>{description || t('featureFlags.dialog.descriptionPlaceholder')}</span>
                  <HoverHint hint={t('featureFlags.dialog.editDescription')}>
                    <button type="button" className={styles.pencil} onClick={() => setEditingDescription(true)} aria-label={t('featureFlags.dialog.editDescription')}>
                      ✎
                    </button>
                  </HoverHint>
                </>
              )}
            </div>

            {detail?.finalizedAt && (
              <div className={styles.finalizedNote}>
                <span className="body-sm-regular">{t('featureFlags.dialog.finalizedNote', { date: new Date(detail.finalizedAt).toLocaleDateString('he-IL') })}</span>
                <button type="button" className={`${styles.link} body-sm-semibold`} onClick={() => setAsking('unfinalize')}>{t('featureFlags.unfinalize')}</button>
              </div>
            )}

            {detail?.retiredAt && (
              <div className={styles.retiredNote}>
                <span className="body-sm-regular">{t('featureFlags.dialog.retiredNote', { date: new Date(detail.retiredAt).toLocaleDateString('he-IL') })}</span>
                <button type="button" className={`${styles.link} body-sm-semibold`} onClick={() => setAsking('unretire')}>{t('featureFlags.unretire')}</button>
              </div>
            )}

            {/* The release in this flag's front: status, how many it reaches, and - only while partial - whom. */}
            <section className={styles.block}>
              <span className="body-sm-semibold">{t(isNedarim ? 'featureFlags.dialog.nedarimSectionTitle' : 'featureFlags.dialog.siteSectionTitle')}</span>
              <div className={styles.statusRow}>
                {segmented(status, setStatus, isLocked)}
                <span className="body-sm-regular text-(--font-body-2)">{coverageLine(status, siteCoverageCount)}</span>
              </div>
              {showTargeting && picker}
            </section>

            {/* What users will see when this is saved. The announcements are the site's: Nedarim has no bell to show them in. */}
            {!isNedarim && (
              <FeatureAnnouncementsSection
                featureKey={featureKey}
                messages={announcements}
                savedStatus={savedStatus}
                selectedStatus={status}
                coverage={{ funds: siteCoverageCount, byPerson: targeting.userEmails.length > 0 || targeting.roles.length > 0 }}
                onEdit={openMessage}
                onAdd={() => openMessage(null)}
              />
            )}

            {error && <p className={`${styles.error} body-sm-regular`}>{error}</p>}

            {footer()}
          </>
        )}
      </div>
    </MainDialogLayout>
  );
};

export default EditFeatureFlagDialog;
